/**
 * The safety outbox — reports and private requests that survive being offline.
 *
 * Only safety reports and private complaint/appeal/privacy requests land here.
 * Creative writes (takes, appreciations, handles, Gauge and predictions) are
 * online-only so they can never cross a later pause or sanction boundary.
 * Safety records are
 * replayed when connectivity returns: on app start, on the browser's
 * `online` event, and whenever a fetch first succeeds again. Rules:
 *   - permanent rejections (HTTP 4xx except cooldown), including an owner
 *     safety pause (423), are dropped —
 *     replaying them would fail forever;
 *   - cooldown (429), server errors (5xx), and network failures stay queued for
 *     the next flush — a transient 5xx during a deploy must not lose a take;
 *   - bounded to 50 device-local records and physically expire after 30 days.
 */

import { retryAfterMs } from './public-reads';

export type OutboxItem =
  | { id?: string; kind: 'take'; body: Record<string, unknown>; ts: number }
  | { id?: string; kind: 'gauge'; matchId: string; body: Record<string, unknown>; ts: number }
  | { id?: string; kind: 'prediction'; matchId: string; body: Record<string, unknown>; ts: number }
  | { id?: string; kind: 'report'; body: Record<string, unknown>; ts: number }
  | { id?: string; kind: 'safety'; body: Record<string, unknown>; ts: number };

const KEY = 'coyg_outbox';
const FAILED_REPORTS_KEY = 'coyg_failed_report_deliveries';
const FAILED_SAFETY_KEY = 'coyg_failed_safety_deliveries';
const STORAGE_GENERATION_KEY = 'coyg_storage_generation';
const LOCAL_DELIVERY_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
export interface FailedSafetyDelivery {
  reference: string;
  kind: 'complaint' | 'appeal' | 'privacy';
  body: string;
  status: number;
  failedAt: number;
}
const ROUTE: Record<OutboxItem['kind'], string> = {
  take: '/api/coyg/takes',
  gauge: '/api/coyg/gauge',
  prediction: '/api/coyg/predictions',
  report: '/api/coyg/reports',
  safety: '/api/coyg/safety-requests',
};

function read(): OutboxItem[] {
  try {
    const items = JSON.parse(localStorage.getItem(KEY) ?? '[]') as OutboxItem[];
    const cutoff = Date.now() - LOCAL_DELIVERY_RETENTION_MS;
    return items.filter((item) => Number.isFinite(item.ts) && item.ts >= cutoff);
  } catch {
    return [];
  }
}

/** Must run only while holding the outbox lock. */
function pruneOutboxLocked(): void {
  try {
    const items = JSON.parse(localStorage.getItem(KEY) ?? '[]') as OutboxItem[];
    const kept = items.filter((item) => (item.kind === 'report' || item.kind === 'safety') &&
      Number.isFinite(item.ts) && item.ts >= Date.now() - LOCAL_DELIVERY_RETENTION_MS);
    if (kept.length === items.length) return;
    localStorage.setItem(KEY, JSON.stringify(kept));
    for (const item of items.filter((candidate) => !kept.includes(candidate))) {
      removeOptimisticTake(item);
      announceExpiredWrite(item.kind);
    }
  } catch {
    try { localStorage.removeItem(KEY); } catch { /* storage denied */ }
  }
}

function boundedQueue(items: OutboxItem[]): { kept: OutboxItem[]; evicted: OutboxItem[] } {
  const ordered = [...items].sort((a, b) => a.ts - b.ts);
  const reports = ordered.filter((item) => item.kind === 'report' || item.kind === 'safety');
  const keptReports = reports.slice(0, 50);
  const remaining = 50 - keptReports.length;
  const lowerStakes = remaining > 0 ? ordered.filter((item) => item.kind !== 'report' && item.kind !== 'safety').slice(-remaining) : [];
  const kept = [...keptReports, ...lowerStakes].sort((a, b) => a.ts - b.ts);
  const keptIds = new Set(kept.map(stableItemId));
  return { kept, evicted: ordered.filter((item) => !keptIds.has(stableItemId(item))) };
}

function write(items: OutboxItem[], requiredId?: string): boolean {
  try {
    const { kept, evicted } = boundedQueue(items);
    const encoded = JSON.stringify(kept);
    localStorage.setItem(KEY, encoded);
    const committed = localStorage.getItem(KEY) === encoded;
    if (!committed) return false;
    for (const item of evicted) {
      if (item.kind === 'report') recordFailedReport(507);
      else if (item.kind === 'safety') recordFailedSafety(item, 507);
      else {
        removeOptimisticTake(item);
        announceWritePersistenceFailure(item.kind);
      }
    }
    return !requiredId || kept.some((item) => item.id === requiredId);
  } catch {
    /* private mode / quota */
    return false;
  }
}

let localLock: Promise<void> = Promise.resolve();
let localPrivacyLock: Promise<void> = Promise.resolve();
const localPrivacyReaders = new Set<Promise<void>>();
let volatileStorageGeneration = 0;
let volatileStorageFallback = false;
let localErasurePending = 0;
async function withOutboxLock<T>(task: () => T | Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('coyg-outbox', { mode: 'exclusive' }, task);
  }
  const previous = localLock;
  let release!: () => void;
  localLock = new Promise<void>((resolve) => { release = resolve; });
  await previous;
  try { return await task(); } finally { release(); }
}

async function withPrivacyLock<T>(task: () => T | Promise<T>, mode: 'shared' | 'exclusive' = 'exclusive'): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('coyg-privacy-boundary', { mode }, task);
  }
  const previous = localPrivacyLock;
  let release!: () => void;
  const completed = new Promise<void>((resolve) => { release = resolve; });
  if (mode === 'shared') {
    localPrivacyReaders.add(completed);
    await previous;
    try { return await task(); } finally { localPrivacyReaders.delete(completed); release(); }
  }
  const readers = [...localPrivacyReaders];
  localPrivacyLock = completed;
  await previous;
  await Promise.all(readers);
  try { return await task(); } finally { release(); }
}

/**
 * Capture device identity and its storage generation under the same lock.
 * An erasure can therefore happen either before this snapshot or after it,
 * never between the identity read and generation capture.
 */
export async function captureLocalCommunitySnapshot<T>(readIdentity: () => T): Promise<{ value: T | null; generation: string }> {
  const blockedAtStart = localErasurePending > 0;
  return withOutboxLock(() => blockedAtStart || localErasurePending > 0
    ? { value: null, generation: 'blocked:erasure' }
    : { value: readIdentity(), generation: captureStorageGenerationLocked() });
}

/** Check a late read without minting a new identity or generation after erasure. */
export async function isLocalCommunityGenerationCurrent(expected: string): Promise<boolean> {
  return withOutboxLock(() => localErasurePending === 0 && currentStorageGenerationLocked() === expected);
}

/**
 * Run an outbound community request only while its identity generation is
 * still current. The lock remains held until transmission settles, so Clear
 * my data either wins before the request (and prevents it) or waits and then
 * erases everything the completed request could have created.
 */
export async function transmitIfLocalCommunityGeneration<T>(
  expectedStorageGeneration: string,
  transmit: () => Promise<T>,
  mode: 'shared' | 'exclusive' = 'exclusive',
): Promise<{ transmitted: false } | { transmitted: true; value: T }> {
  return withPrivacyLock(async () => {
    const current = await withOutboxLock(() => localErasurePending === 0
      && currentStorageGenerationLocked() === expectedStorageGeneration);
    if (!current) return { transmitted: false };
    return { transmitted: true, value: await transmit() };
  }, mode);
}

/**
 * Return a durable generation token while holding the outbox lock. Erasure
 * removes this token with the rest of the COYG data, so an in-flight flush in
 * this or another tab can tell that its pre-erasure snapshot is stale.
 */
function captureStorageGenerationLocked(): string {
  try {
    let generation = localStorage.getItem(STORAGE_GENERATION_KEY);
    if (!generation) {
      generation = crypto.randomUUID();
      localStorage.setItem(STORAGE_GENERATION_KEY, generation);
      if (localStorage.getItem(STORAGE_GENERATION_KEY) !== generation) throw new Error('storage unavailable');
    }
    volatileStorageFallback = false;
    return `durable:${generation}`;
  } catch {
    volatileStorageFallback = true;
    return `volatile:${volatileStorageGeneration}`;
  }
}

function currentStorageGenerationLocked(): string {
  if (volatileStorageFallback) return `volatile:${volatileStorageGeneration}`;
  try {
    const generation = localStorage.getItem(STORAGE_GENERATION_KEY);
    return generation ? `durable:${generation}` : 'durable:erased';
  } catch {
    return `volatile:${volatileStorageGeneration}`;
  }
}

/** Must run only while holding the outbox lock. */
function clearLocalCommunityStorageLocked(): void {
    // Always advance the in-memory fallback before clearing persistent state.
    // This covers private-mode/storage-denied browsers as far as they permit.
    volatileStorageGeneration += 1;
    try {
      for (let index = localStorage.length - 1; index >= 0; index -= 1) {
        const key = localStorage.key(index);
        if (key?.startsWith('coyg_') || (key?.startsWith('coyg:') && key.includes(':takes:'))) localStorage.removeItem(key);
      }
    } catch {
      /* private mode — there may be no persistent identity to clear */
    }
    try { window.dispatchEvent(new Event('coyg:local-data-cleared')); } catch { /* non-browser */ }
}

/** Clear device identity and every cached/queued COYG record atomically. */
export async function clearLocalCommunityStorage(): Promise<void> {
  await withPrivacyLock(() => withOutboxLock(clearLocalCommunityStorageLocked));
}

/**
 * Serialize server erasure with every safety transmission, then clear this
 * device before releasing the same lock. A request cannot cross the erasure
 * boundary in either direction.
 */
export async function eraseLocalCommunityData(eraseServerData: () => Promise<boolean>): Promise<boolean> {
  localErasurePending += 1;
  try {
    return await withPrivacyLock(async () => {
      const erased = await eraseServerData();
      if (erased) await withOutboxLock(clearLocalCommunityStorageLocked);
      return erased;
    });
  } finally {
    localErasurePending -= 1;
  }
}

export async function queueWrite(item: OutboxItem, expectedStorageGeneration?: string): Promise<boolean> {
  if (item.kind !== 'report' && item.kind !== 'safety') return false;
  return withOutboxLock(() => {
    // A report/request assembled from an older identity snapshot must never
    // recreate COYG data after Clear my data has advanced the generation.
    if (localErasurePending > 0 || (expectedStorageGeneration && currentStorageGenerationLocked() !== expectedStorageGeneration)) return false;
    pruneOutboxLocked();
    let items = read();
    const queued = { ...item, id: item.id ?? crypto.randomUUID() } as OutboxItem;
    items.push(queued);
    return write(items, queued.id);
  });
}

/** Remove one acknowledged/terminal operation from the durable queue. */
export async function removeQueuedWrite(id: string, expectedStorageGeneration?: string): Promise<boolean> {
  return withOutboxLock(() => {
    if (expectedStorageGeneration && currentStorageGenerationLocked() !== expectedStorageGeneration) return false;
    pruneOutboxLocked();
    return write(read().filter((item) => item.id !== id));
  });
}

function recordFailedReport(status: number): void {
  try {
    const failures = JSON.parse(localStorage.getItem(FAILED_REPORTS_KEY) ?? '[]') as Array<{ status: number; failedAt: number }>;
    localStorage.setItem(FAILED_REPORTS_KEY, JSON.stringify([...failures, { status, failedAt: Date.now() }].slice(-10)));
    window.dispatchEvent(new CustomEvent('coyg:report-delivery-failed'));
  } catch { /* the in-app event is best effort when storage is unavailable */ }
}

export async function consumeFailedReportDeliveries(): Promise<number> {
  return withOutboxLock(() => {
    try {
      const failures = (JSON.parse(localStorage.getItem(FAILED_REPORTS_KEY) ?? '[]') as Array<{ failedAt?: number }>)
        .filter((failure) => Number(failure.failedAt) >= Date.now() - LOCAL_DELIVERY_RETENTION_MS);
      localStorage.removeItem(FAILED_REPORTS_KEY);
      return failures.length;
    } catch { return 0; }
  });
}

function recordFailedSafety(item: Extract<OutboxItem, { kind: 'safety' }>, status: number): void {
  try {
    const existing = readFailedSafetyDeliveries();
    const kind = ['complaint', 'appeal', 'privacy'].includes(String(item.body.kind))
      ? item.body.kind as FailedSafetyDelivery['kind']
      : 'complaint';
    const failure: FailedSafetyDelivery = {
      reference: item.id ?? (typeof item.body.clientId === 'string' ? item.body.clientId : crypto.randomUUID()),
      kind,
      body: typeof item.body.body === 'string' ? item.body.body : '',
      status,
      failedAt: Date.now(),
    };
    localStorage.setItem(FAILED_SAFETY_KEY, JSON.stringify([...existing.filter((record) => record.reference !== failure.reference), failure].slice(-10)));
    window.dispatchEvent(new CustomEvent('coyg:safety-delivery-failed'));
  } catch { /* the queue event remains best effort when storage is unavailable */ }
}

export function readFailedSafetyDeliveries(): FailedSafetyDelivery[] {
  try {
    const records = JSON.parse(localStorage.getItem(FAILED_SAFETY_KEY) ?? '[]') as FailedSafetyDelivery[];
    return records.filter((record) => record && typeof record.reference === 'string' && typeof record.body === 'string' &&
      Number(record.failedAt) >= Date.now() - LOCAL_DELIVERY_RETENTION_MS);
  } catch {
    return [];
  }
}

export async function clearFailedSafetyDelivery(reference: string): Promise<void> {
  await withOutboxLock(() => {
    try {
      localStorage.setItem(FAILED_SAFETY_KEY, JSON.stringify(readFailedSafetyDeliveries().filter((record) => record.reference !== reference)));
    } catch { /* private mode */ }
  });
}

export function announceWritePersistenceFailure(kind: OutboxItem['kind']): void {
  try { window.dispatchEvent(new CustomEvent('coyg:write-persistence-failed', { detail: { kind } })); } catch { /* non-browser */ }
}

function announceExpiredWrite(kind: OutboxItem['kind']): void {
  try { window.dispatchEvent(new CustomEvent('coyg:write-persistence-failed', { detail: { kind, expired: true } })); } catch { /* non-browser */ }
}

export function pendingWrites(): number {
  return read().length;
}

/** Physically enforce the disclosed device-retention limit on every app boot. */
export async function pruneLocalDeliveryStorage(): Promise<void> {
  await withOutboxLock(() => {
    pruneOutboxLocked();
    try {
      const records = JSON.parse(localStorage.getItem(FAILED_SAFETY_KEY) ?? '[]') as FailedSafetyDelivery[];
      const kept = records.filter((record) => record && typeof record.reference === 'string' && typeof record.body === 'string' &&
        Number(record.failedAt) >= Date.now() - LOCAL_DELIVERY_RETENTION_MS);
      if (kept.length !== records.length) localStorage.setItem(FAILED_SAFETY_KEY, JSON.stringify(kept));
    } catch {
      try { localStorage.removeItem(FAILED_SAFETY_KEY); } catch { /* storage denied */ }
    }
    try {
      const records = JSON.parse(localStorage.getItem(FAILED_REPORTS_KEY) ?? '[]') as Array<{ failedAt?: number }>;
      const kept = records.filter((record) => Number(record.failedAt) >= Date.now() - LOCAL_DELIVERY_RETENTION_MS);
      if (kept.length !== records.length) localStorage.setItem(FAILED_REPORTS_KEY, JSON.stringify(kept));
    } catch {
      try { localStorage.removeItem(FAILED_REPORTS_KEY); } catch { /* storage denied */ }
    }
  });
}

let flushing = false;
let automaticRetryAt = 0;
let automaticFailures = 0;

function removeOptimisticTake(item: OutboxItem): void {
  if (item.kind !== 'take' || typeof item.body.clientId !== 'string') return;
  try {
    const locals = JSON.parse(localStorage.getItem('coyg_local_takes') ?? '[]') as Array<Record<string, unknown>>;
    localStorage.setItem('coyg_local_takes', JSON.stringify(locals.filter((take) => take.id !== item.body.clientId)));
  } catch {
    /* private mode / malformed legacy cache */
  }
}

function reconcileTakeReplay(item: OutboxItem, result: unknown): void {
  if (item.kind !== 'take' || !result || typeof result !== 'object') return;
  const response = result as { take?: Record<string, unknown>; handleChanged?: unknown; clientId?: unknown };
  const clientId = typeof response.clientId === 'string'
    ? response.clientId
    : typeof item.body.clientId === 'string' ? item.body.clientId : null;
  try {
    if (typeof response.handleChanged === 'string') localStorage.setItem('coyg_handle', response.handleChanged);
    removeOptimisticTake(item);
    if (response.take && typeof response.take.id === 'string') {
      const cached = JSON.parse(localStorage.getItem('coyg_takes_cache') ?? '[]') as Array<Record<string, unknown>>;
      const next = [response.take, ...cached.filter((take) => take.id !== response.take!.id && take.id !== clientId)];
      localStorage.setItem('coyg_takes_cache', JSON.stringify(next.slice(0, 50)));
    }
  } catch {
    /* private mode / malformed legacy cache — server remains authoritative */
  }
}

function stableItemId(item: OutboxItem): string {
  return item.id ?? `legacy:${item.kind}:${item.ts}:${'matchId' in item ? item.matchId : ''}:${JSON.stringify(item.body)}`;
}

function coalesceLatest(items: OutboxItem[]): OutboxItem[] {
  const output: OutboxItem[] = [];
  for (const item of items.sort((a, b) => a.ts - b.ts)) {
    if (item.kind === 'gauge' || item.kind === 'prediction') {
      const index = output.findIndex((existing) => existing.kind === item.kind && 'matchId' in existing && existing.matchId === item.matchId);
      if (index >= 0) output.splice(index, 1);
    }
    if (!output.some((existing) => stableItemId(existing) === stableItemId(item))) output.push(item);
  }
  return output;
}

/** Replay the queue. Safe to call eagerly — it no-ops while already running. */
export async function flushOutbox(apiBase = '', automatic = false): Promise<{ sent: number; kept: number }> {
  if (automatic && Date.now() < automaticRetryAt) return { sent: 0, kept: read().length };
  if (flushing) return { sent: 0, kept: read().length };
  flushing = true;
  try {
    const snapshot = await withOutboxLock(() => {
      pruneOutboxLocked();
      return { items: read(), storageGeneration: captureStorageGenerationLocked() };
    });
    const { items, storageGeneration } = snapshot;
    if (items.length === 0) { automaticRetryAt = 0; automaticFailures = 0; return { sent: 0, kept: 0 }; }
    const kept: OutboxItem[] = [];
    const failedReports: number[] = [];
    const failedSafety: Array<{ item: Extract<OutboxItem, { kind: 'safety' }>; status: number }> = [];
    let sent = 0;
    let retryDelay = 0;
    for (const item of items) {
      try {
        const delivery = await transmitIfLocalCommunityGeneration(storageGeneration, () => fetch(`${apiBase}${ROUTE[item.kind]}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(item.body),
          signal: AbortSignal.timeout(10_000),
        }));
        if (!delivery.transmitted) break;
        const res = delivery.value;
        if (res.ok) {
          if (item.kind === 'take') {
            const result = await res.json().catch(() => null);
            reconcileTakeReplay(item, result);
          }
          sent += 1;
        } else if (res.status === 429 || res.status >= 500) {
          // Cooldown or a server-side error (deploy blip, 502/503) — both are
          // transient and may succeed on retry, so keep the item queued.
          kept.push(item);
          retryDelay = Math.max(retryDelay, retryAfterMs(res.headers.get('retry-after')), 60_000);
        } else if (item.kind === 'report') {
          failedReports.push(res.status);
        } else if (item.kind === 'safety') {
          failedSafety.push({ item, status: res.status });
        } else if (item.kind === 'take') {
          // A terminal replay rejection must also remove the optimistic local
          // row; otherwise blocked/closed content looks publicly posted forever.
          removeOptimisticTake(item);
        } else {
          announceWritePersistenceFailure(item.kind);
        }
        // a definitive 4xx (400/403/409/410/423/…) is permanently invalid:
        // drop it. In particular, writes composed before an owner safety pause
        // must not silently publish when the community later reopens.
      } catch {
        kept.push(item); // still offline
        retryDelay = Math.max(retryDelay, 60_000);
      }
    }
    // Writes can arrive while the network loop awaits. Preserve everything
    // that was not in the original snapshot, then re-run latest-state
    // coalescing so a new Gauge/prediction supersedes an older retained one.
    const next = await withOutboxLock(() => {
      // Erasure owns the privacy boundary. Never let an older network request
      // recreate its queue entry or a failed-delivery copy of its private body.
      if (currentStorageGenerationLocked() !== storageGeneration) return read();
      pruneOutboxLocked();
      for (const status of failedReports) recordFailedReport(status);
      for (const failure of failedSafety) recordFailedSafety(failure.item, failure.status);
      const snapshotIds = new Set(items.map(stableItemId));
      const current = read();
      const currentIds = new Set(current.map(stableItemId));
      const survivingKept = kept.filter((item) => currentIds.has(stableItemId(item)));
      const arrivedDuringFlush = current.filter((item) => !snapshotIds.has(stableItemId(item)));
      const merged = coalesceLatest([...survivingKept, ...arrivedDuringFlush]);
      write(merged);
      return merged;
    });
    if (retryDelay > 0) {
      automaticFailures += 1;
      automaticRetryAt = Date.now() + Math.max(retryDelay, Math.min(60 * 60_000, 60_000 * 2 ** Math.min(automaticFailures - 1, 6)));
    } else { automaticRetryAt = 0; automaticFailures = 0; }
    return { sent, kept: next.length };
  } finally {
    flushing = false;
  }
}

/** Wire the standard triggers once per session. */
export function armOutbox(apiBase = ''): void {
  try {
    const maintain = async () => {
      await pruneLocalDeliveryStorage();
      if (navigator.onLine && document.visibilityState !== 'hidden') await flushOutbox(apiBase, true);
    };
    void pruneLocalDeliveryStorage();
    const retry = () => { void maintain(); };
    window.addEventListener('online', retry);
    // App start: give the first paint a beat, then replay.
    setTimeout(retry, 2500);
    // A 429/5xx can recover without the browser firing `online`. Retry the
    // durable queue at a restrained cadence so safety reports do not stall.
    // Retention pruning still runs while offline.
    setInterval(retry, 60_000);
  } catch {
    /* non-browser environment */
  }
}
